import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';

import { EUsageMetrics } from '@feathr/blackbox';
import { Alert, showError, Tab } from '@feathr/components';
import type { IPageProps } from '@feathr/extender/App/Page';
import Page from '@feathr/extender/App/Page';
import { FeatureUsageLimit } from '@feathr/extender/components';
import { useFeatureLimits } from '@feathr/extender/hooks';
import {
  useAccount,
  useLocalUrl,
  useRole,
  useStore,
  useUsageAndQuota,
} from '@feathr/extender/state';

import UsageStat, { usageMap } from './UsageStat';

import * as styles from './DataPage.css';

interface IGroupPermissions {
  canClone: boolean;
  canEdit: boolean;
  isReadOnly: boolean;
}

export interface IDataPageProps extends IPageProps {
  filters?: React.ReactNode;
  /** If the page is part of the groups section of the app (rather than people) */
  isGroupsSection?: boolean;
  hasTabs?: boolean;
  hideUsageSection?: boolean;
  permissions?: IGroupPermissions;
  sectionTitle: string;
  /**
   * Width of legacy main page content
   * @deprecated Child components should handle width
   */
  width?: 'narrow' | 'wide' | 'full';
}

function DataPage({
  description,
  children,
  filters,
  isGroupsSection = false,
  hasTabs = true,
  hideUsageSection = false,
  permissions,
  sectionTitle,
  title,
  ...props
}: IDataPageProps): JSX.Element {
  const account = useAccount();
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const [knownPeopleCount, setKnownPeopleCount] = useState<number>(0);
  const [knownPeopleLoading, setKnownPeopleLoading] = useState<boolean>(false);
  const { UsageReports } = useStore();

  const { hasSegmentsSome, hasSegments } = useRole();
  const { isContactLimitsEnabled } = useFeatureLimits();

  const { quota, usage, showUsage } = useUsageAndQuota(account.id, [
    EUsageMetrics.IncludedAnonymousPersons,
    EUsageMetrics.IncludedIdentifiedPersons,
    EUsageMetrics.Breadcrumbs,
  ]);
  useEffect(() => {
    async function getData(): Promise<void> {
      try {
        setKnownPeopleLoading(true);
        const count = await UsageReports.getKnownPeopleCount();
        setKnownPeopleCount(count);
      } catch (error) {
        showError(error, t);
      } finally {
        setKnownPeopleLoading(false);
      }
    }

    void getData();
  }, []);

  // Is the superpixel set up?
  if (!account.isPending && !account.hasActiveSuperpixel && !account.isPlatformLite) {
    return <Redirect to={localUrl('/data/pixel?nodata=1')} />;
  }

  const tabs = [
    <Tab key={'people'} link={'people'} title={t('People')} />,
    <Tab key={'activity'} link={'activity'} title={t('Activity')} />,
  ];
  if (isGroupsSection) {
    tabs.push(<Tab key={'analytics'} link={'analytics'} title={t('Analytics')} />);

    if ((hasSegmentsSome && permissions?.canEdit) || hasSegments) {
      tabs.push(<Tab key={'settings'} link={'settings'} title={t('Settings')} />);
    }
  }

  return (
    <Page
      {...props}
      loading={account.isPending}
      tabs={hasTabs ? tabs : undefined}
      title={sectionTitle}
    >
      {title && <h3 className={styles.heading}>{title}</h3>}
      {/* Optionally hide the usage section if we don't want the stats rendering. */}
      {!hideUsageSection && showUsage && (
        <div className={styles.usageContainer} key={'usage'}>
          {Object.keys(usage).map(
            (key: string): JSX.Element => (
              <UsageStat
                key={key}
                limit={(quota[key] && numeral(quota[key]).format('0,0')) ?? t('unlimited')}
                t={t}
                title={usageMap(t, key as EUsageMetrics)}
                usage={numeral(usage[key]).format('0,0')}
              />
            ),
          )}
        </div>
      )}
      {isContactLimitsEnabled && (
        <FeatureUsageLimit loading={knownPeopleLoading} type={'contacts'} used={knownPeopleCount} />
      )}
      <>
        {!account.isPending && !account.hasActiveSuperpixel && account.isPlatformLite && (
          <Alert
            className={styles.alert}
            description={t(
              'Data may take up to 24 hours to appear after placing your Super Pixel.',
            )}
            title={t('Still processing')}
            type={'info'}
          />
        )}

        {!!filters && <div className={classNames(styles.filters)}>{filters}</div>}
        {!!description && <div className={styles.description}>{description}</div>}
        {children}
      </>
    </Page>
  );
}

export default observer(DataPage);
